// Room-tape calendar (Gantt-style booking grid).
// Rows = room types (sorted). Each row has 1+ lanes so overlapping bookings
// for the same room type stack instead of colliding.
// All inline `.map()` iterations hoisted out of JSX per Babel plugin quirk.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ChevronLeft, ChevronRight, RefreshCw, Loader2, Calendar as CalIcon,
  MoreHorizontal, ChevronDown, ChevronUp, ExternalLink, TrendingUp, Euro, Sparkles, X, Rocket,
} from 'lucide-react';
import { Beds24ReconnectBanner } from '../../components/admin/Beds24ReconnectBanner';
import { apiCall } from './_shared';
import CalendarContextMenu from './CalendarContextMenu';
import RoomMoveModal from './RoomMoveModal';
import GuestMessageModal from './GuestMessageModal';
import RevenueAutopilot from './RevenueAutopilot';

// Day-column width shrinks as the window grows so 30d fits without scrolling
// forever. Same pattern Beds24 uses.
const DAY_PX_FOR = (days) => (days >= 30 ? 46 : days >= 14 ? 68 : 96);
const LANE_PX = 34;              // height of one booking lane
const ROW_MIN_PX = 48;           // min row height when a room has 0 bookings
const UNIT_ROW_PX = 32;          // Beds24-style: one row per physical unit
const HEADER_ROW_PX = 42;        // room-type header row (availability count + price)

const dayName = (iso) => new Date(iso + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short' });
const dayNum = (iso) => new Date(iso + 'T00:00:00').getDate();
const monthName = (iso) => new Date(iso + 'T00:00:00').toLocaleDateString('en-GB', { month: 'short' });
const isWeekend = (iso) => {
  const d = new Date(iso + 'T00:00:00').getDay();
  return d === 0 || d === 6;
};
const isToday = (iso) => iso === new Date().toISOString().slice(0, 10);

const shiftDate = (iso, delta) => {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() + delta);
  return d.toISOString().slice(0, 10);
};

// Booking colour by source. Source strings from Beds24 vary — we normalize.
const srcColor = (src) => {
  const s = (src || '').toLowerCase();
  if (s.includes('airbnb')) return { bg: 'bg-rose-500', ring: 'ring-rose-300' };
  if (s.includes('booking')) return { bg: 'bg-blue-500', ring: 'ring-blue-300' };
  if (s.includes('expedia')) return { bg: 'bg-yellow-500', ring: 'ring-yellow-300' };
  if (s.includes('agoda')) return { bg: 'bg-red-500', ring: 'ring-red-300' };
  if (s.includes('direct') || s === '') return { bg: 'bg-emerald-500', ring: 'ring-emerald-300' };
  if (s.includes('ota')) return { bg: 'bg-purple-500', ring: 'ring-purple-300' };
  return { bg: 'bg-orange-500', ring: 'ring-orange-300' };
};

const initials = (name) => (name || 'G')
  .split(/\s+/)
  .slice(0, 2)
  .map((p) => p[0])
  .join('')
  .toUpperCase();

const CalendarDashboard = ({ userRole }) => {
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [startDate, setStartDate] = useState(today);
  const [days, setDays] = useState(14);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [selected, setSelected] = useState(null);

  // Filters
  const [hideFull, setHideFull] = useState(false);
  const [arrivalsToday, setArrivalsToday] = useState(false);
  const [srcFilter, setSrcFilter] = useState('');   // 'direct'|'airbnb'|'booking'|'expedia'|''
  const [query, setQuery] = useState('');
  const [collapsed, setCollapsed] = useState(() => new Set());  // Beds24-style: room-types collapsible

  const toggleCollapsed = useCallback((roomId) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(roomId)) next.delete(roomId);
      else next.add(roomId);
      return next;
    });
  }, []);

  // Drag-to-reschedule state
  const [dragging, setDragging] = useState(null);   // {booking, startX}
  const [ghost, setGhost] = useState(null);
  const [hoverLane, setHoverLane] = useState(null);         // preview offset delta (days)
  const [saving, setSaving] = useState(false);

  // Per-cell width shrinks as the window grows so 30d actually fits.
  const dayPx = useMemo(() => DAY_PX_FOR(days), [days]);

  // Housekeeping status change (inline from calendar)
  const [statusPicker, setStatusPicker] = useState(null); // {lane, row}

  // New-booking modal state
  const [newBooking, setNewBooking] = useState(null); // {row, lane, start_iso, end_iso}
  const [nbForm, setNbForm] = useState({ guest_name: '', email: '', phone: '', adults: 2, children: 0, notes: '' });
  const [nbSaving, setNbSaving] = useState(false);
  const [nbSuggestions, setNbSuggestions] = useState([]);

  // Rates map: {room_id: {iso_date: nightly_price}}
  const [rates, setRates] = useState({});

  // Click-drag date range on empty cells
  const [dragRange, setDragRange] = useState(null); // {row, lane, startIdx, endIdx}
  const dragRangeRef = React.useRef(null);
  useEffect(() => { dragRangeRef.current = dragRange; }, [dragRange]);

  // Guest history modal (deep-link from autocomplete)
  const [guestHistory, setGuestHistory] = useState(null); // {name, loading, data}
  const [newTag, setNewTag] = useState({ label: '', kind: 'note' });

  // Rate-edit modal + drag state on group-header row
  const [rateEdit, setRateEdit] = useState(null); // {row, start_iso, end_iso, price, saving}
  const [rateDrag, setRateDrag] = useState(null); // {row, startIdx, endIdx}
  const rateDragRef = React.useRef(null);
  useEffect(() => { rateDragRef.current = rateDrag; }, [rateDrag]);

  // Rate optimizer modal
  const [optimizer, setOptimizer] = useState(null); // {loading, data}
  const [showAutopilot, setShowAutopilot] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const [j, r] = await Promise.all([
        apiCall(`/api/admin/reception/calendar?start_date=${startDate}&days=${days}`),
        apiCall(`/api/admin/reception/calendar/rates?start_date=${startDate}&days=${days}`).catch(() => ({ rates: {} })),
      ]);
      setData(j);
      setRates(r.rates || {});
      const ds = j?.window?.dates || [];
      if (ds.length) {
        const endD = new Date(ds[ds.length - 1] + 'T00:00:00'); endD.setDate(endD.getDate() + 1);
        apiCall(`/api/admin/reception/calendar/notes?start=${ds[0]}&end=${endD.toISOString().slice(0, 10)}`)
          .then((n) => setNotes(n.notes || [])).catch(() => {});
      }
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, [startDate, days]);

  // ---------- Sticky notes per day ----------
  const [notes, setNotes] = useState([]);
  const [notesDay, setNotesDay] = useState(null);   // iso date whose popover is open
  const [noteDraft, setNoteDraft] = useState({ text: '', kind: 'info' });
  const NOTE_STYLE = { info: 'bg-sky-100 text-sky-800 border-sky-200', vip: 'bg-amber-100 text-amber-800 border-amber-200', group: 'bg-violet-100 text-violet-800 border-violet-200', maintenance: 'bg-slate-200 text-slate-800 border-slate-300', warning: 'bg-red-100 text-red-800 border-red-200' };
  const notesFor = (iso) => notes.filter((n) => n.date === iso);
  const addNote = async () => {
    if (noteDraft.text.trim().length < 1) return;
    try {
      const n = await apiCall('/api/admin/reception/calendar/notes', { method: 'POST', body: JSON.stringify({ date: notesDay, ...noteDraft, text: noteDraft.text.trim() }) });
      setNotes((arr) => [...arr, n]); setNoteDraft({ text: '', kind: 'info' });
    } catch (e) { alert(e.message); }
  };
  const deleteNote = async (id) => {
    try { await apiCall(`/api/admin/reception/calendar/notes/${id}`, { method: 'DELETE' }); setNotes((arr) => arr.filter((n) => n.id !== id)); }
    catch (e) { alert(e.message); }
  };

  const setUnitStatus = useCallback(async (unitKey, status) => {
    try {
      await apiCall(`/api/admin/housekeeping/status/unit/${unitKey}`, {
        method: 'PUT', body: JSON.stringify({ status }),
      });
      setStatusPicker(null);
      await load();
    } catch (e) { alert(`Status update failed: ${e.message}`); }
  }, [load]);

  const cancelBooking = useCallback(async (bookingId, guestName) => {
    if (!window.confirm(`Cancel booking for ${guestName || 'this guest'}?\nThis will mark it cancelled in Beds24 and remove it from every connected OTA.`)) return;
    try {
      await apiCall(`/api/admin/reception/calendar/bookings/${bookingId}/cancel`, { method: 'POST' });
      setSelected(null);
      await load();
    } catch (e) { alert(`Cancel failed: ${e.message}`); }
  }, [load]);

  // ---------- Right-click quick actions ----------
  const [menu, setMenu] = useState(null);            // {x, y, booking, row, lane}
  const [menuStatus, setMenuStatus] = useState('loading');
  const [menuBusy, setMenuBusy] = useState(false);
  const [quickMove, setQuickMove] = useState(null);  // booking for RoomMoveModal
  const [quickMsg, setQuickMsg] = useState(null);    // booking for GuestMessageModal

  const openMenu = useCallback(async (e, b, row, lane) => {
    e.preventDefault();
    e.stopPropagation();
    setMenu({ x: e.clientX, y: e.clientY, booking: b, row, lane });
    setMenuStatus('loading');
    try {
      const f = await apiCall(`/api/admin/reception/folios/${b.booking_id}`);
      setMenuStatus(f.status || 'reserved');
    } catch { setMenuStatus('none'); }
  }, []);

  const ensureFolio = useCallback(async (b, row, lane) => {
    try {
      return await apiCall(`/api/admin/reception/folios/${b.booking_id}`);
    } catch {
      return apiCall(`/api/admin/reception/folios/${b.booking_id}/init`, {
        method: 'POST',
        body: JSON.stringify({
          guest_name: b.guest_name, room_id: row.room_id, unit_id: lane?.unassigned ? null : lane?.unit_number ?? b.unit_id ?? null,
          room_name: row.room_name, check_in: b.check_in, check_out: b.check_out, adults: b.adults || 2, children: b.children || 0,
        }),
      });
    }
  }, []);

  const onMenuAction = useCallback(async (action, b) => {
    const { row, lane } = menu || {};
    if (action === 'cancel') { setMenu(null); cancelBooking(b.booking_id, b.guest_name); return; }
    setMenuBusy(true);
    try {
      const folio = await ensureFolio(b, row, lane);
      if (action === 'checkin') {
        const nights = Math.max(1, Math.round((new Date(b.check_out) - new Date(b.check_in)) / 86400000) || 1);
        let tax = 0;
        try { tax = (await apiCall(`/api/admin/finance/tourist-tax/quote?adults=${b.adults || 2}&children=${b.children || 0}&nights=${nights}`)).amount || 0; } catch { /* optional */ }
        if (!window.confirm(`Check in ${b.guest_name} to ${lane?.unit_name || row.room_name}?
Tourist tax to collect: €${tax.toFixed(2)}`)) return;
        await apiCall(`/api/admin/reception/folios/${b.booking_id}/check-in`, { method: 'PUT', body: JSON.stringify({ tourist_tax_collected: tax }) });
        setMenu(null); await load();
      } else if (action === 'checkout') {
        const bal = Number(folio.balance || 0);
        if (bal > 0.009 && !window.confirm(`${b.guest_name} still owes €${bal.toFixed(2)}. Check out anyway?`)) return;
        await apiCall(`/api/admin/reception/folios/${b.booking_id}/check-out`, { method: 'PUT' });
        setMenu(null); await load();
      } else if (action === 'message') {
        setMenu(null); setQuickMsg(b);
      } else if (action === 'move') {
        setMenu(null); setQuickMove(b);
      }
    } catch (err) { alert(`Action failed: ${err.message}`); }
    finally { setMenuBusy(false); }
  }, [menu, ensureFolio, cancelBooking, load]);

  // Debounced guest-name autocomplete lookup for the New Booking modal.
  useEffect(() => {
    if (!newBooking) return;
    const q = (nbForm.guest_name || '').trim();
    if (q.length < 2) { setNbSuggestions([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await apiCall(`/api/admin/reception/guests/search?q=${encodeURIComponent(q)}`);
        setNbSuggestions(r.items || []);
      } catch { setNbSuggestions([]); }
    }, 220);
    return () => clearTimeout(t);
  }, [nbForm.guest_name, newBooking]);

  // Commit the drag-selected date range as a new-booking intent.
  const commitDragRange = useCallback(() => {
    const dr = dragRangeRef.current;
    if (!dr) return;
    dragRangeRef.current = null;
    setDragRange(null);
    const lo = Math.min(dr.startIdx, dr.endIdx);
    const hi = Math.max(dr.startIdx, dr.endIdx);
    // Abort if any covered date is already booked.
    const conflict = dr.lane.bookings.some((b) => !(b.start_offset + b.span_days <= lo || b.start_offset > hi));
    if (conflict) return;
    const dateArr = data?.dates || [];
    const ci = dateArr[lo];
    if (!ci) return;
    const coDate = new Date(dateArr[hi] + 'T00:00:00');
    coDate.setDate(coDate.getDate() + 1);
    const co = coDate.toISOString().slice(0, 10);
    setNewBooking({ row: dr.row, lane: dr.lane, start_iso: ci, end_iso: co });
    setNbForm({ guest_name: '', email: '', phone: '', adults: 2, children: 0, notes: '' });
  }, [data]);

  // Global mouseup: commits drag regardless of which element the pointer
  // released on (fixes drag ending on a booking pill or the row-border).
  useEffect(() => {
    const onUp = () => {
      if (dragRangeRef.current) commitDragRange();
      // Commit rate-drag on group header rows too.
      if (rateDragRef.current) {
        const rd = rateDragRef.current;
        rateDragRef.current = null;
        setRateDrag(null);
        const lo = Math.min(rd.startIdx, rd.endIdx);
        const hi = Math.max(rd.startIdx, rd.endIdx);
        const dateArr = data?.dates || [];
        if (dateArr[lo] && dateArr[hi]) {
          const currentPrice = (rates[rd.row.room_id] || {})[dateArr[lo]] || '';
          setRateEdit({
            row: rd.row,
            start_iso: dateArr[lo],
            end_iso: dateArr[hi],
            price: currentPrice ? String(Math.round(currentPrice)) : '',
            saving: false,
          });
        }
      }
    };
    window.addEventListener('mouseup', onUp);
    return () => window.removeEventListener('mouseup', onUp);
  }, [commitDragRange, data, rates]);

  const openGuestHistory = useCallback(async (name) => {
    setGuestHistory({ name, loading: true, data: null });
    try {
      const r = await apiCall(`/api/admin/reception/guests/history?name=${encodeURIComponent(name)}`);
      setGuestHistory({ name, loading: false, data: r });
    } catch (e) {
      setGuestHistory({ name, loading: false, data: { history: [], error: e.message } });
    }
  }, []);

  const saveGuestTags = useCallback(async (newTags) => {
    if (!guestHistory) return;
    try {
      const r = await apiCall('/api/admin/reception/guests/tags', {
        method: 'PUT',
        body: JSON.stringify({ name: guestHistory.name, tags: newTags }),
      });
      setGuestHistory((prev) => prev ? { ...prev, data: { ...prev.data, tags: r.tags } } : prev);
      setNewTag({ label: '', kind: 'note' });
    } catch (e) {
      alert(`Tag update failed: ${e.message}`);
    }
  }, [guestHistory]);

  const openOptimizer = useCallback(async () => {
    setOptimizer({ loading: true, data: null });
    try {
      const r = await apiCall('/api/admin/reception/rates/optimizer?weeks_back=4');
      setOptimizer({ loading: false, data: r });
    } catch (e) {
      setOptimizer({ loading: false, data: { suggestions: [], error: e.message } });
    }
  }, []);

  const applyOptimizerRow = useCallback((s) => {
    if (!s.suggested_rate) return;
    setOptimizer(null);
    setRateEdit({
      row: { room_id: s.room_id, room_name: s.room_name },
      start_iso: s.next_weekend_start,
      end_iso: s.next_weekend_end,
      price: String(s.suggested_rate),
      saving: false,
    });
  }, []);

  const submitRateEdit = useCallback(async () => {
    if (!rateEdit) return;
    const price = parseFloat(rateEdit.price);
    if (isNaN(price) || price < 0) { alert('Enter a valid price.'); return; }
    setRateEdit({ ...rateEdit, saving: true });
    try {
      await apiCall('/api/admin/reception/calendar/rates', {
        method: 'POST',
        body: JSON.stringify({
          room_id: rateEdit.row.room_id,
          start_date: rateEdit.start_iso,
          end_date: rateEdit.end_iso,
          price,
        }),
      });
      setRateEdit(null);
      await load();
    } catch (e) {
      alert(`Rate update failed: ${e.message}`);
      setRateEdit((prev) => prev ? { ...prev, saving: false } : null);
    }
  }, [rateEdit, load]);

  const submitNewBooking = useCallback(async () => {
    if (!newBooking || !nbForm.guest_name.trim()) return;
    setNbSaving(true);
    try {
      await apiCall('/api/admin/reception/calendar/bookings', {
        method: 'POST',
        body: JSON.stringify({
          room_id: newBooking.row.room_id,
          unit_id: newBooking.lane.unit_number,
          check_in: newBooking.start_iso,
          check_out: newBooking.end_iso,
          guest_name: nbForm.guest_name.trim(),
          email: nbForm.email || null,
          phone: nbForm.phone || null,
          adults: parseInt(nbForm.adults || 1, 10),
          children: parseInt(nbForm.children || 0, 10),
          notes: nbForm.notes || null,
        }),
      });
      setNewBooking(null);
      setNbForm({ guest_name: '', email: '', phone: '', adults: 2, children: 0, notes: '' });
      await load();
    } catch (e) {
      alert(`Create booking failed: ${e.message}`);
    } finally {
      setNbSaving(false);
    }
  }, [newBooking, nbForm, load]);

  useEffect(() => { load(); }, [load]);

  // Apply filters client-side (backend already returns everything).
  const rawRows = data?.rows || [];
  const rows = useMemo(() => {
    if (!rawRows.length) return rawRows;
    const q = query.trim().toLowerCase();
    return rawRows
      .map((r) => {
        let lanes = r.lanes;
        if (srcFilter) {
          lanes = lanes.map((lane) => ({ ...lane, bookings: lane.bookings.filter((b) => (b.source || '').toLowerCase().includes(srcFilter)) })).filter((l) => l.bookings.length > 0);
        }
        if (arrivalsToday) {
          lanes = lanes.map((lane) => ({ ...lane, bookings: lane.bookings.filter((b) => b.check_in === today) })).filter((l) => l.bookings.length > 0);
        }
        if (q) {
          // Keep only lanes whose room# / room-type / guest name match.
          lanes = lanes.filter((lane) => {
            const inLane = (lane.unit_name || `${r.room_id}.${lane.unit_number}`).toString().toLowerCase().includes(q);
            const inType = (r.room_name || '').toLowerCase().includes(q);
            const inGuest = lane.bookings.some((b) => (b.guest_name || '').toLowerCase().includes(q));
            return inLane || inType || inGuest;
          });
        }
        return { ...r, lanes };
      })
      .filter((r) => {
        const total = r.lanes.reduce((s, l) => s + l.bookings.length, 0);
        if (arrivalsToday && total === 0) return false;
        if (srcFilter && total === 0) return false;
        if (q && r.lanes.length === 0) return false;
        if (hideFull) {
          // "Fully booked" here = every date in window covered by ≥1 booking.
          const dayCovered = new Array(days).fill(false);
          r.lanes.forEach((lane) => lane.bookings.forEach((b) => {
            for (let i = b.start_offset; i < b.start_offset + b.span_days; i++) {
              if (i >= 0 && i < days) dayCovered[i] = true;
            }
          }));
          if (dayCovered.every(Boolean)) return false;
        }
        return true;
      });
  }, [rawRows, srcFilter, arrivalsToday, hideFull, today, days, query]);

  const dates = data?.window?.dates || [];

  // ---------- Drag-to-reschedule ----------
  // ---------- Drag-to-resize (change arrival / departure) ----------
  const [resizing, setResizing] = useState(null);      // {booking, edge, startX}
  const [resizeGhost, setResizeGhost] = useState(0);   // delta days
  const onResizeStart = (e, b, edge) => {
    e.preventDefault();
    e.stopPropagation();
    setResizing({ booking: b, edge, startX: e.clientX });
    setResizeGhost(0);
  };
  useEffect(() => {
    if (!resizing) return;
    const onMove = (e) => setResizeGhost(Math.round((e.clientX - resizing.startX) / dayPx));
    const onUp = async (e) => {
      const delta = Math.round((e.clientX - resizing.startX) / dayPx);
      const { booking: b, edge } = resizing;
      setResizing(null);
      setResizeGhost(0);
      if (!delta) return;
      const d = new Date((edge === 'end' ? b.check_out : b.check_in) + 'T00:00:00');
      d.setDate(d.getDate() + delta);
      const body = edge === 'end'
        ? { check_in: b.check_in, check_out: d.toISOString().slice(0, 10) }
        : { check_in: d.toISOString().slice(0, 10), check_out: b.check_out };
      if (body.check_out <= body.check_in) { alert('A stay needs at least one night.'); return; }
      const nights = Math.round((new Date(body.check_out) - new Date(body.check_in)) / 86400000);
      if (!window.confirm(`${edge === 'end' ? 'Change departure' : 'Change arrival'} for "${b.guest_name}":\n${body.check_in} → ${body.check_out} (${nights} night${nights === 1 ? '' : 's'})\n\nThis will sync to Beds24.`)) return;
      setSaving(true);
      try {
        await apiCall(`/api/admin/reception/calendar/bookings/${b.booking_id}`, { method: 'PATCH', body: JSON.stringify(body) });
        await load();
      } catch (err) { alert(`Resize failed: ${err.message}`); }
      finally { setSaving(false); }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [resizing, dayPx, load]);

  const onDragStart = (e, b, lane, row) => {
    e.preventDefault();
    setDragging({ booking: b, startX: e.clientX, fromLane: lane, fromRoom: row });
    setGhost(0);
    setHoverLane(null);
  };

  // Which unit lane is under the pointer? (drag vertically to another room)
  const laneAtPoint = (x, y) => {
    const el = document.elementFromPoint(x, y);
    const laneEl = el && el.closest ? el.closest('[data-lane-room]') : null;
    if (!laneEl) return null;
    const roomId = Number(laneEl.getAttribute('data-lane-room'));
    const unitRaw = laneEl.getAttribute('data-lane-unit');
    if (!unitRaw) return null;  // "Unassigned" lanes are not a drop target
    return { room_id: roomId, unit_id: Number(unitRaw), unit_name: laneEl.getAttribute('data-lane-name') || `#${unitRaw}`, room_name: laneEl.getAttribute('data-lane-room-name') || '' };
  };

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => {
      const dx = e.clientX - dragging.startX;
      const daysDelta = Math.round(dx / dayPx);
      setGhost(daysDelta);
      const tgt = laneAtPoint(e.clientX, e.clientY);
      const same = tgt && tgt.room_id === dragging.fromRoom.room_id && tgt.unit_id === dragging.fromLane.unit_number;
      setHoverLane(tgt && !same ? tgt : null);
    };
    const onUp = async (e) => {
      const dx = e.clientX - dragging.startX;
      const daysDelta = Math.round(dx / dayPx);
      const b = dragging.booking;
      const tgt = laneAtPoint(e.clientX, e.clientY);
      const unitChanged = !!tgt && !(tgt.room_id === dragging.fromRoom.room_id && tgt.unit_id === dragging.fromLane.unit_number);
      setDragging(null);
      setGhost(null);
      setHoverLane(null);
      if (!daysDelta && !unitChanged) return;
      const body = {};
      const lines = [];
      if (daysDelta) {
        const ci = new Date(b.check_in + 'T00:00:00');
        ci.setDate(ci.getDate() + daysDelta);
        const co = new Date(b.check_out + 'T00:00:00');
        co.setDate(co.getDate() + daysDelta);
        body.check_in = ci.toISOString().slice(0, 10);
        body.check_out = co.toISOString().slice(0, 10);
        lines.push(`dates ${b.check_in} → ${body.check_in} (${daysDelta > 0 ? '+' : ''}${daysDelta} days)`);
      }
      if (unitChanged) {
        body.room_id = tgt.room_id;
        body.unit_id = tgt.unit_id;
        lines.push(`room ${dragging.fromLane.unit_name || '#' + dragging.fromLane.unit_number} → ${tgt.unit_name}${tgt.room_name ? ` (${tgt.room_name})` : ''}`);
      }
      if (!window.confirm(`Move "${b.guest_name}":\n• ${lines.join('\n• ')}\n\nThis will sync to Beds24.`)) return;
      setSaving(true);
      const patch = async (force) => apiCall(`/api/admin/reception/calendar/bookings/${b.booking_id}`, {
        method: 'PATCH', body: JSON.stringify(force ? { ...body, force: true } : body),
      });
      try {
        try {
          await patch(false);
        } catch (err) {
          if (/dirty|cleaning|Beds24/i.test(err.message) && window.confirm(`${err.message}\n\nForce the move anyway?`)) await patch(true);
          else throw err;
        }
        await load();
      } catch (err) { alert(`Move failed: ${err.message}`); }
      finally { setSaving(false); }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [dragging, load]);

  // Beds24-style status pill per unit (compact).
  const HK_STYLE = {
    clean:     { bg: 'bg-emerald-50',  ring: 'bg-emerald-500', tx: 'text-emerald-800', pill: 'bg-emerald-500 text-white',  label: 'clean' },
    cleaning:  { bg: 'bg-sky-50',      ring: 'bg-sky-500',     tx: 'text-sky-800',     pill: 'bg-sky-500 text-white',      label: 'cleaning' },
    dirty:     { bg: 'bg-amber-50',    ring: 'bg-amber-500',   tx: 'text-amber-900',   pill: 'bg-amber-400 text-amber-950', label: 'dirty' },
    to_repair: { bg: 'bg-yellow-50',   ring: 'bg-yellow-500',  tx: 'text-yellow-900',  pill: 'bg-yellow-400 text-yellow-950', label: 'to repair' },
    inspected: { bg: 'bg-sky-50',      ring: 'bg-sky-500',     tx: 'text-sky-800',     pill: 'bg-sky-500 text-white',      label: 'inspected' },
    occupied:  { bg: 'bg-orange-50',   ring: 'bg-orange-500',  tx: 'text-orange-900',  pill: 'bg-orange-500 text-white',   label: 'occupied' },
    ooo:       { bg: 'bg-slate-100',   ring: 'bg-slate-500',   tx: 'text-slate-700',   pill: 'bg-slate-500 text-white',    label: 'out of order' },
  };

  // Flatten rows into a Beds24-style sequence of visual rows:
  //   {type: 'group', row, availableByDay: [n, n, ...]}
  //   {type: 'unit',  row, lane, laneIndex}
  // Collapsed groups only emit the group row.
  const visualRows = useMemo(() => {
    const out = [];
    for (const r of rows) {
      const inv = r.inventory || r.lanes.length || 1;
      const availableByDay = dates.map((iso, dayIdx) => {
        // Booked = # of lanes whose bookings cover this day.
        // Available = inventory - booked (never negative).
        let booked = 0;
        for (const lane of r.lanes) {
          if (lane.bookings.some((b) => dayIdx >= b.start_offset && dayIdx < b.start_offset + b.span_days)) {
            booked += 1;
          }
        }
        return Math.max(0, inv - booked);
      });
      out.push({ type: 'group', row: r, availableByDay });
      if (!collapsed.has(r.room_id)) {
        r.lanes.forEach((lane, laneIndex) => {
          out.push({ type: 'unit', row: r, lane, laneIndex });
        });
      }
    }
    return out;
  }, [rows, dates, collapsed]);

  // Property-wide occupancy % per day — powers the top KPI row.
  // Denominator = true physical inventory across all room types (from
  // db.rooms.num_units — 83 for La Rosa Negra), NOT just lanes-with-bookings.
  const occupancyByDay = useMemo(() => {
    const totalUnits = rows.reduce((s, r) => s + (r.inventory || r.lanes.length || 0), 0);
    if (!totalUnits) return dates.map(() => ({ pct: 0, booked: 0, total: 0 }));
    return dates.map((_, dayIdx) => {
      let booked = 0;
      for (const r of rows) {
        const inv = r.inventory || r.lanes.length || 0;
        let laneBooked = 0;
        for (const lane of r.lanes) {
          if (lane.bookings.some((b) => dayIdx >= b.start_offset && dayIdx < b.start_offset + b.span_days)) {
            laneBooked += 1;
          }
        }
        // Clamp per room-type — Beds24 sometimes emits duplicate unit_ids that
        // would otherwise push a type over its true inventory.
        booked += Math.min(laneBooked, inv);
      }
      const pct = Math.min(100, Math.round((booked / totalUnits) * 100));
      return { pct, booked, total: totalUnits };
    });
  }, [rows, dates]);

  // Grid header row: date columns.
  const headerCells = dates.map((iso) => (
    <div
      key={iso}
      className={`border-b border-l border-slate-200 py-1 text-center ${
        isToday(iso) ? 'bg-orange-100' : isWeekend(iso) ? 'bg-slate-50' : 'bg-white'
      }`}
      style={{ width: `${dayPx}px`, minWidth: `${dayPx}px` }}
      data-testid={`cal-hdr-${iso}`}
    >
      <button onClick={() => { setNotesDay(iso); setNoteDraft({ text: '', kind: 'info' }); }} className="w-full hover:bg-orange-50/60 rounded" title="Sticky notes for this day" data-testid={`cal-notes-btn-${iso}`}>
        <p className="text-[10px] uppercase text-slate-500">{dayName(iso)}</p>
        <p className={`text-sm font-bold ${isToday(iso) ? 'text-orange-700' : 'text-slate-900'}`}>{dayNum(iso)}</p>
        <p className="text-[10px] text-slate-400 flex items-center justify-center gap-0.5">
          {monthName(iso)}
          {notesFor(iso).length > 0 && (
            <span className={`ml-0.5 inline-flex items-center justify-center min-w-[14px] h-[14px] rounded-sm text-[9px] font-bold border ${NOTE_STYLE[notesFor(iso)[0].kind] || NOTE_STYLE.info}`} data-testid={`cal-notes-count-${iso}`}>{notesFor(iso).length}</span>
          )}
        </p>
      </button>
    </div>
  ));

  const notesPopover = notesDay && (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-24 bg-black/30" onClick={() => setNotesDay(null)}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-4 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="cal-notes-popover">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-slate-900">Notes · {notesDay}</h3>
          <button onClick={() => setNotesDay(null)} className="p-1 rounded hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>
        <ul className="space-y-1.5 max-h-60 overflow-y-auto">
          {notesFor(notesDay).map((n) => (
            <li key={n.id} className={`flex items-start justify-between gap-2 border rounded-lg px-2.5 py-1.5 text-sm ${NOTE_STYLE[n.kind] || NOTE_STYLE.info}`} data-testid={`cal-note-${n.id}`}>
              <span><span className="text-[10px] uppercase font-bold mr-1">{n.kind}</span>{n.text}<span className="block text-[10px] opacity-70">{n.by} · {n.at.slice(0, 16).replace('T', ' ')}</span></span>
              <button onClick={() => deleteNote(n.id)} className="text-xs opacity-60 hover:opacity-100" title="Delete" data-testid={`cal-note-delete-${n.id}`}>✕</button>
            </li>
          ))}
          {notesFor(notesDay).length === 0 && <li className="text-xs text-slate-400">No notes for this day yet.</li>}
        </ul>
        <div className="flex gap-2">
          <select value={noteDraft.kind} onChange={(e) => setNoteDraft({ ...noteDraft, kind: e.target.value })} className="border rounded px-2 text-xs" data-testid="cal-note-kind">
            {['info', 'vip', 'group', 'maintenance', 'warning'].map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
          <input value={noteDraft.text} onChange={(e) => setNoteDraft({ ...noteDraft, text: e.target.value })} onKeyDown={(e) => e.key === 'Enter' && addNote()} placeholder="e.g. Group of 12 arriving 15:00 · VIP in 716" className="flex-1 border rounded px-2 py-1.5 text-sm" maxLength={300} data-testid="cal-note-text" />
          <button onClick={addNote} className="px-3 py-1.5 rounded bg-orange-500 text-white text-sm font-semibold" data-testid="cal-note-add">Pin</button>
        </div>
      </div>
    </div>
  );

  // Grid body rows: Beds24-style — one row per visual row (group header or
  // physical unit). Each row is split into: left sticky label + N date cells.
  const renderRow = (v, idx) => {
    if (v.type === 'group') {
      const r = v.row;
      const total = r.lanes.reduce((s, l) => s + l.bookings.length, 0);
      const isOpen = !collapsed.has(r.room_id);
      const risk = r.turnover_risk;
      return (
        <div
          key={`grp-${r.room_id}`}
          className="flex border-b border-slate-300 bg-slate-50"
          style={{ minHeight: `${HEADER_ROW_PX}px` }}
          data-testid={`cal-room-${r.room_id}`}
        >
          <div
            className={`sticky left-0 z-10 bg-slate-50 hover:bg-slate-100 flex items-center gap-1.5 px-2 border-r border-slate-300 cursor-pointer ${risk ? 'border-l-4 border-l-red-500' : ''}`}
            style={{ width: '260px', minWidth: '260px' }}
            onClick={() => toggleCollapsed(r.room_id)}
          >
            {isOpen ? <ChevronDown className="w-3.5 h-3.5 text-slate-500 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-500 shrink-0" />}
            <p className="text-sm font-semibold text-slate-900 truncate flex-1" title={r.room_name}>
              {r.room_name}
            </p>
            <span className="text-[10px] font-semibold text-slate-500 shrink-0">
              {r.lanes.length}
            </span>
            {risk && (
              <span
                className="text-[9px] uppercase tracking-wider font-bold text-red-600 bg-red-50 border border-red-200 rounded px-1 py-0.5 shrink-0"
                data-testid={`cal-risk-${r.room_id}`}
                title={`Housekeeping · arrival today`}
              >
                ⚠
              </span>
            )}
          </div>
          {dates.map((iso, dayIdx) => {
            const avail = v.availableByDay[dayIdx];
            const full = avail === 0;
            const price = (rates[r.room_id] || {})[iso];
            const inRateDrag =
              rateDrag && rateDrag.row.room_id === r.room_id &&
              dayIdx >= Math.min(rateDrag.startIdx, rateDrag.endIdx) &&
              dayIdx <= Math.max(rateDrag.startIdx, rateDrag.endIdx);
            return (
              <div
                key={iso}
                onMouseDown={(e) => {
                  if (e.button !== 0) return;
                  e.stopPropagation();
                  e.preventDefault();
                  const rd = { row: r, startIdx: dayIdx, endIdx: dayIdx };
                  rateDragRef.current = rd;
                  setRateDrag(rd);
                }}
                onMouseEnter={() => {
                  const rd = rateDragRef.current;
                  if (!rd || rd.row.room_id !== r.room_id) return;
                  const next = { ...rd, endIdx: dayIdx };
                  rateDragRef.current = next;
                  setRateDrag(next);
                }}
                onClick={(e) => {
                  // Fallback: single-tap opens rate-edit for that one day.
                  // Ignore if a drag range was already committed.
                  if (rateDragRef.current) return;
                  e.stopPropagation();
                  const currentPrice = (rates[r.room_id] || {})[iso] || '';
                  setRateEdit({
                    row: r,
                    start_iso: iso,
                    end_iso: iso,
                    price: currentPrice ? String(Math.round(currentPrice)) : '',
                    saving: false,
                  });
                }}
                className={`border-l border-slate-200 flex flex-col items-center justify-center leading-tight cursor-pointer relative select-none ${
                  inRateDrag ? 'bg-emerald-300/70 ring-2 ring-emerald-500 z-10'
                    : full ? 'bg-slate-800 text-slate-300'
                    : 'text-slate-700 hover:bg-emerald-50'
                } ${isToday(iso) && !inRateDrag ? 'bg-orange-100' : isWeekend(iso) && !full && !inRateDrag ? 'bg-slate-100' : ''}`}
                style={{ width: `${dayPx}px`, minWidth: `${dayPx}px` }}
                title={full ? 'Fully booked — bump the rate!' : 'Drag to bulk-set rate'}
              >
                <span className="text-[11px] font-semibold">{avail}</span>
                {price ? (
                  <span className={`text-[9px] font-medium ${full ? 'text-slate-400' : inRateDrag ? 'text-emerald-900' : 'text-emerald-700'}`}>
                    €{Math.round(price)}
                  </span>
                ) : null}
                {full && !inRateDrag && (
                  <span
                    className="absolute -top-1 -right-1 bg-red-500 text-white text-[8px] font-bold uppercase leading-none px-1 py-0.5 rounded shadow"
                    title="Fully booked — consider bumping the rate for this date"
                    data-testid={`cal-overbook-${r.room_id}-${iso}`}
                  >
                    +€
                  </span>
                )}
              </div>
            );
          })}
        </div>
      );
    }
    // Unit row
    const { row: r, lane, laneIndex } = v;
    const style = HK_STYLE[lane.housekeeping_status] || HK_STYLE.clean;
    // Blocks for this specific lane, laid out along the date columns.
    const laneBlocks = lane.bookings.map((b) => {
      const c = srcColor(b.source);
      const isDragged = dragging && dragging.booking.booking_id === b.booking_id;
      const shift = isDragged ? (ghost || 0) : 0;
      const isResized = resizing && resizing.booking.booking_id === b.booking_id;
      const rEnd = isResized && resizing.edge === 'end' ? Math.max(1 - b.span_days, resizeGhost || 0) : 0;
      const rStart = isResized && resizing.edge === 'start' ? Math.min(b.span_days - 1, resizeGhost || 0) : 0;
      // Hotel convention: bar starts at the MIDDLE of the check-in day
      // (right half = arrival) and ends at the MIDDLE of the check-out day
      // (left half = departure). Two guests can share a day cell — one
      // departing on the morning half, one arriving on the afternoon half.
      // When the booking overflows the visible window we hug the edge
      // instead so the ChevronLeft/Right cue remains flush.
      const half = dayPx / 2;
      const leftInset = b.extends_left ? 0 : half;
      const rightInset = b.extends_right ? 0 : half;
      return (
        <button
          key={b.booking_id + '-' + b.start_offset}
          onMouseDown={(e) => onDragStart(e, b, lane, r)}
          onClick={(e) => { e.stopPropagation(); if (!isDragged) setSelected(b); }}
          onContextMenu={(e) => openMenu(e, b, r, lane)}
          className={`${c.bg} hover:brightness-110 focus:outline-none focus:ring-2 ${c.ring} absolute rounded-full shadow-sm text-white text-[11px] font-medium truncate px-2 flex items-center gap-1 cursor-grab active:cursor-grabbing pointer-events-auto ${isDragged ? 'opacity-70 ring-2 ring-white z-20' : ''}`}
          style={{
            left: `${(b.start_offset + shift + rStart) * dayPx + leftInset + 3}px`,
            width: `${(b.span_days + rEnd - rStart) * dayPx - leftInset + rightInset - 6}px`,
            top: '4px',
            height: `${UNIT_ROW_PX - 8}px`,
          }}
          data-testid={`cal-booking-${b.booking_id}`}
          title={`${b.guest_name} · ${b.check_in} → ${b.check_out} · ${b.source} · drag to move · edges to resize · right-click for actions`}
        >
          {b.extends_left && <ChevronLeft className="w-3 h-3 -ml-1 flex-shrink-0 opacity-80" />}
          <span className="truncate flex-1 text-left">
            {b.span_days >= 2 ? b.guest_name : initials(b.guest_name)}
          </span>
          {b.extends_right && <ChevronRight className="w-3 h-3 -mr-1 flex-shrink-0 opacity-80" />}
          {!b.extends_left && (
            <span
              onMouseDown={(e) => onResizeStart(e, b, 'start')}
              className="absolute left-0 top-0 h-full w-2.5 cursor-ew-resize rounded-l-full hover:bg-white/40"
              title="Drag to change arrival"
              data-testid={`cal-resize-start-${b.booking_id}`}
            />
          )}
          {!b.extends_right && (
            <span
              onMouseDown={(e) => onResizeStart(e, b, 'end')}
              className="absolute right-0 top-0 h-full w-2.5 cursor-ew-resize rounded-r-full hover:bg-white/40"
              title="Drag to extend / shorten the stay"
              data-testid={`cal-resize-end-${b.booking_id}`}
            />
          )}
        </button>
      );
    });
    const laneLabel = lane.unit_name || `#${lane.unit_number}`;
    const isDropTarget = !!hoverLane && hoverLane.room_id === r.room_id && !lane.unassigned && hoverLane.unit_id === lane.unit_number;
    return (
      <div
        key={lane.unit_key}
        className={`flex border-b border-slate-200 relative ${style.bg} ${isDropTarget ? 'ring-2 ring-inset ring-orange-500 bg-orange-50' : ''}`}
        style={{ minHeight: `${UNIT_ROW_PX}px` }}
        data-testid={`cal-unit-row-${lane.unit_key}`}
        data-lane-room={r.room_id}
        data-lane-unit={lane.unassigned ? '' : lane.unit_number}
        data-lane-name={lane.unit_name || ''}
        data-lane-room-name={r.room_name || r.name || ''}
      >
        <div
          className={`sticky left-0 z-10 ${style.bg} flex items-center px-2 border-r border-slate-300 gap-2`}
          style={{ width: '260px', minWidth: '260px' }}
        >
          <span className={`w-1 self-stretch ${style.ring}`}></span>
          <span className="text-xs font-bold text-slate-800 min-w-[36px]">{laneLabel}</span>
          {lane.unassigned ? (
            <span className="ml-auto text-[9px] font-bold uppercase tracking-wider text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded" title="Booking has no room number in Beds24 — drag it onto a room">no room</span>
          ) : (
            <button
              onClick={(e) => { e.stopPropagation(); setStatusPicker({ lane, row: r }); }}
              className={`ml-auto text-[9px] font-bold uppercase tracking-wider ${style.pill} px-1.5 py-0.5 rounded hover:brightness-110 active:scale-95 cursor-pointer`}
              data-testid={`cal-hk-${lane.unit_key}`}
              title="Change housekeeping status"
            >
              {style.label}
            </button>
          )}
        </div>
        {/* Date cells with weekend / today shading — click / drag to create a booking */}
        {dates.map((iso, dayIdx) => {
          const occupied = lane.bookings.some((b) => dayIdx >= b.start_offset && dayIdx < b.start_offset + b.span_days);
          const inDragRange =
            dragRange && dragRange.lane.unit_key === lane.unit_key &&
            dayIdx >= Math.min(dragRange.startIdx, dragRange.endIdx) &&
            dayIdx <= Math.max(dragRange.startIdx, dragRange.endIdx);
          return (
            <div
              key={iso}
              onMouseDown={(e) => {
                if (occupied || e.button !== 0) return;
                e.preventDefault();
                const dr = { row: r, lane, startIdx: dayIdx, endIdx: dayIdx };
                dragRangeRef.current = dr;
                setDragRange(dr);
              }}
              onMouseEnter={() => {
                const dr = dragRangeRef.current;
                if (!dr || dr.lane.unit_key !== lane.unit_key) return;
                const next = { ...dr, endIdx: dayIdx };
                dragRangeRef.current = next;
                setDragRange(next);
              }}
              onClick={() => {
                // Fallback: if the drag wasn't captured (e.g. programmatic click
                // in tests, or a very fast tap on touch devices), open the
                // 1-night new-booking modal directly.
                if (occupied || dragRangeRef.current) return;
                const coDate = new Date(iso + 'T00:00:00'); coDate.setDate(coDate.getDate() + 1);
                setNewBooking({ row: r, lane, start_iso: iso, end_iso: coDate.toISOString().slice(0, 10) });
                setNbForm({ guest_name: '', email: '', phone: '', adults: 2, children: 0, notes: '' });
              }}
              className={`border-l border-slate-200 select-none ${
                inDragRange ? 'bg-orange-300/60'
                  : isToday(iso) ? 'bg-orange-50/50'
                  : isWeekend(iso) ? 'bg-slate-100/50'
                  : ''
              } ${occupied ? '' : 'hover:bg-orange-100/60 cursor-pointer'}`}
              style={{ width: `${dayPx}px`, minWidth: `${dayPx}px` }}
              title={occupied ? '' : 'Click a cell or drag across multiple days to create a booking'}
              data-testid={occupied ? undefined : `cal-empty-${lane.unit_key}-${iso}`}
            />
          );
        })}
        {/* Absolute-positioned booking blocks overlaid on the date grid.
            Overlay itself is click-through so empty cells below stay clickable;
            only the booking buttons are click-targets. */}
        <div
          className="absolute pointer-events-none"
          style={{
            left: '260px',
            width: `${dates.length * dayPx}px`,
            height: `${UNIT_ROW_PX}px`,
          }}
        >
          <div className="relative w-full h-full">
            {laneBlocks}
          </div>
        </div>
      </div>
    );
  };

  const legend = [
    { label: 'Direct', src: 'direct' },
    { label: 'Airbnb', src: 'airbnb' },
    { label: 'Booking.com', src: 'booking' },
    { label: 'Expedia', src: 'expedia' },
    { label: 'Other', src: 'other' },
  ].map((l) => {
    const c = srcColor(l.src);
    return (
      <span key={l.src} className="inline-flex items-center gap-1 text-xs text-slate-600">
        <span className={`w-3 h-3 rounded-sm ${c.bg}`} />
        {l.label}
      </span>
    );
  });

  const winBtns = [7, 14, 30].map((d) => (
    <button
      key={d}
      onClick={() => setDays(d)}
      className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${
        days === d ? 'bg-orange-500 text-white border-orange-500' : 'bg-white border-slate-200 text-slate-700'
      }`}
      data-testid={`cal-window-${d}`}
    >
      {d}d
    </button>
  ));

  const totalWidth = dates.length * dayPx;

  return (
    <div className="space-y-4" data-testid="calendar-dashboard">
      {data?.beds24_error && <Beds24ReconnectBanner error={data.beds24_error} dataSource={data.data_source} userRole={userRole} onReconnected={load} />}
      <div className="bg-white rounded-xl border p-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-bold text-slate-900 flex items-center gap-2">
              <CalIcon className="w-5 h-5 text-orange-500" /> Room calendar
            </h2>
            <p className="text-xs text-slate-500 mt-1">
              {data?.window?.start_date} → {data?.window?.end_date} · {data?.total_bookings || 0} bookings
              {data?.data_source === 'mirror' && <span className="ml-2 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-800" data-testid="calendar-data-source">local copy</span>}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setStartDate(shiftDate(startDate, -Math.floor(days / 2)))}
              className="p-2 rounded-full bg-slate-100 hover:bg-slate-200"
              data-testid="cal-prev"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="text-sm border rounded px-2 py-1"
              data-testid="cal-start-date"
            />
            <button
              onClick={() => setStartDate(shiftDate(startDate, Math.floor(days / 2)))}
              className="p-2 rounded-full bg-slate-100 hover:bg-slate-200"
              data-testid="cal-next"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => setStartDate(today)}
              className="px-3 py-1.5 rounded-full text-xs font-semibold border bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
              data-testid="cal-today"
            >
              Today
            </button>
            {winBtns}
            <button
              onClick={load}
              disabled={loading}
              className="p-2 rounded-full bg-slate-100 hover:bg-slate-200"
              data-testid="cal-refresh"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </button>
            <button
              onClick={openOptimizer}
              className="px-3 py-2 rounded-full bg-gradient-to-r from-emerald-500 to-emerald-600 text-white text-xs font-bold flex items-center gap-1.5 hover:shadow-lg active:scale-95"
              data-testid="cal-optimizer-open"
              title="Auto-suggest weekend rate hikes from the last 4 weekends"
            >
              <Sparkles className="w-4 h-4" /> Rate Optimizer
            </button>
            <button
              onClick={() => setShowAutopilot(true)}
              className="px-3 py-2 rounded-full bg-slate-900 text-white text-xs font-bold flex items-center gap-1.5 hover:shadow-lg active:scale-95"
              data-testid="cal-autopilot-open"
              title="Forward rate proposals for the next 30 days from live Beds24 pickup, lead time, weekends and local events"
            >
              <Rocket className="w-4 h-4 text-emerald-300" /> Autopilot
            </button>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-4 flex-wrap">{legend}</div>
        <div className="mt-3 flex items-center gap-2 flex-wrap border-t pt-3">
          <span className="text-xs uppercase tracking-widest text-slate-500 font-bold">Filters:</span>
          <button
            onClick={() => setArrivalsToday((v) => !v)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${arrivalsToday ? 'bg-orange-500 text-white border-orange-500' : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'}`}
            data-testid="cal-filter-arrivals-today"
          >
            Arrivals today
          </button>
          <button
            onClick={() => setHideFull((v) => !v)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${hideFull ? 'bg-orange-500 text-white border-orange-500' : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'}`}
            data-testid="cal-filter-hide-full"
          >
            Hide fully-booked
          </button>
          <span className="mx-1 text-slate-300">|</span>
          {['', 'direct', 'airbnb', 'booking', 'expedia'].map((s) => (
            <button
              key={s || 'all'}
              onClick={() => setSrcFilter(s)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${srcFilter === s ? 'bg-slate-800 text-white border-slate-800' : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'}`}
              data-testid={`cal-filter-src-${s || 'all'}`}
            >
              {s === '' ? 'All sources' : s[0].toUpperCase() + s.slice(1)}
            </button>
          ))}
          {saving && <span className="text-xs text-amber-600 ml-2 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Saving…</span>}
          <div className="ml-auto flex items-center gap-2 border rounded-full px-3 py-1 bg-white focus-within:ring-2 focus-within:ring-orange-300">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search room# or guest…"
              className="text-xs bg-transparent outline-none w-40"
              data-testid="cal-room-search"
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                className="text-slate-400 hover:text-slate-700 text-xs"
                data-testid="cal-room-search-clear"
                aria-label="clear"
              >×</button>
            )}
          </div>
        </div>
      </div>

      {err && (
        <p className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{err}</p>
      )}

      <div className="bg-white rounded-xl border overflow-hidden">
        {loading && !data ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center py-16 text-slate-400" data-testid="cal-empty">
            <CalIcon className="w-10 h-10 mx-auto mb-2" />
            <p className="text-sm">No rooms to display.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            {/* Sticky top header — Beds24 style: one grid row shared by all body rows */}
            <div className="sticky top-0 z-30 flex bg-white border-b border-slate-300 shadow-sm">
              <div
                className="sticky left-0 z-30 bg-white flex items-end px-3 py-2 border-r border-slate-300"
                style={{ width: '260px', minWidth: '260px' }}
              >
                <p className="text-xs uppercase tracking-widest text-slate-500 font-bold">Room</p>
              </div>
              <div className="flex">{headerCells}</div>
            </div>
            {/* Property-wide occupancy % row — night-by-night hit rate */}
            <div className="flex border-b border-slate-300 bg-gradient-to-r from-slate-900 to-slate-800">
              <div
                className="sticky left-0 z-20 bg-gradient-to-r from-slate-900 to-slate-800 flex items-center px-3 border-r border-slate-700"
                style={{ width: '260px', minWidth: '260px', minHeight: '32px' }}
                data-testid="cal-occupancy-label"
              >
                <p className="text-[10px] uppercase tracking-widest text-slate-300 font-bold">Occupancy</p>
              </div>
              {dates.map((iso, dayIdx) => {
                const o = occupancyByDay[dayIdx] || { pct: 0, booked: 0, total: 0 };
                // Colour ramp: green ≥85, amber ≥60, slate below.
                const clr =
                  o.pct >= 85 ? 'text-emerald-300'
                  : o.pct >= 60 ? 'text-amber-300'
                  : o.pct >= 30 ? 'text-orange-300'
                  : 'text-slate-400';
                const bg = isToday(iso) ? 'bg-orange-900/40' : '';
                return (
                  <div
                    key={iso}
                    className={`border-l border-slate-700 flex items-center justify-center ${bg}`}
                    style={{ width: `${dayPx}px`, minWidth: `${dayPx}px`, minHeight: '32px' }}
                    title={`${o.booked} / ${o.total} rooms occupied`}
                    data-testid={`cal-occ-${iso}`}
                  >
                    <span className={`text-xs font-bold ${clr}`}>{o.pct}%</span>
                  </div>
                );
              })}
            </div>
            {/* Body: one row per group / unit */}
            <div>
              {visualRows.map((v, idx) => renderRow(v, idx))}
            </div>
          </div>
        )}
      </div>

      {/* Rate Optimizer modal — data-driven weekend rate suggestions */}
      {showAutopilot && <RevenueAutopilot onClose={() => setShowAutopilot(false)} />}
      {optimizer && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          onClick={() => setOptimizer(null)}
          data-testid="cal-optimizer-modal"
        >
          <div
            className="bg-white rounded-xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-emerald-500 to-emerald-700 text-white p-4 flex justify-between items-start">
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-widest opacity-90 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> Weekly Rate Optimizer
                </p>
                <h3 className="text-xl font-bold">
                  {optimizer.data
                    ? `Next weekend · ${optimizer.data.next_weekend_start} → ${optimizer.data.next_weekend_end}`
                    : 'Analysing…'}
                </h3>
                <p className="text-xs opacity-90 mt-0.5">
                  Based on the last {optimizer.data?.weeks_sampled || 4} Fri/Sat pairs · <span className="font-semibold">commission-weighted</span>.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOptimizer(null)}
                className="p-1 rounded hover:bg-white/20"
              >
                <ChevronLeft className="w-5 h-5 rotate-45" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {optimizer.loading ? (
                <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-emerald-500" /></div>
              ) : (optimizer.data?.suggestions || []).length === 0 ? (
                <p className="text-sm text-slate-500 italic text-center py-8">
                  {optimizer.data?.error || 'No suggestions right now — try again once Beds24 responds.'}
                </p>
              ) : (
                <ul className="space-y-2">
                  {optimizer.data.suggestions.map((s) => {
                    const isHike = s.delta_pct > 0;
                    const isCut  = s.delta_pct < 0;
                    const badgeCls = isHike ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                                   : isCut  ? 'bg-red-50 text-red-700 border-red-200'
                                            : 'bg-slate-100 text-slate-600 border-slate-200';
                    const trendClr = s.trend === 'rising' ? 'text-emerald-600'
                                  : s.trend === 'falling' ? 'text-red-500'
                                                          : 'text-slate-400';
                    return (
                      <li
                        key={s.room_id}
                        className="border rounded-lg p-3 hover:border-emerald-300 flex gap-3 items-center"
                        data-testid={`cal-opt-row-${s.room_id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-slate-900 truncate">{s.room_name}</p>
                          <p className="text-xs text-slate-500 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                            <span>raw <span className="font-bold text-slate-700">{s.avg_occupancy}%</span></span>
                            <span>·</span>
                            <span title="Commission-adjusted effective occupancy">
                              eff <span className="font-bold text-emerald-700">{s.effective_occupancy}%</span>
                            </span>
                            {s.ota_share_pct > 0 && (
                              <span className="text-[10px] uppercase font-semibold bg-red-50 text-red-700 border border-red-200 px-1 py-0.5 rounded" title="Share of OTA nights in the sample">
                                {s.ota_share_pct}% OTA · −{s.avg_commission_pct}%
                              </span>
                            )}
                            <span className={`text-[10px] uppercase font-bold ${trendClr}`}>{s.trend}</span>
                            {s.current_rate ? (
                              <span>· €{Math.round(s.current_rate)} → <span className={`font-bold ${isHike ? 'text-emerald-700' : isCut ? 'text-red-600' : ''}`}>€{s.suggested_rate}</span></span>
                            ) : null}
                          </p>
                        </div>
                        <span
                          className={`text-sm font-black tracking-tight px-2 py-1 rounded border ${badgeCls} min-w-[52px] text-center`}
                          data-testid={`cal-opt-delta-${s.room_id}`}
                        >
                          {s.delta_pct > 0 ? '+' : ''}{s.delta_pct}%
                        </span>
                        {s.suggested_rate && s.delta_pct !== 0 && (
                          <button
                            type="button"
                            onClick={() => applyOptimizerRow(s)}
                            className="text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded"
                            data-testid={`cal-opt-apply-${s.room_id}`}
                          >
                            Apply
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            <p className="text-[10px] text-slate-400 px-4 py-2 border-t bg-slate-50">
              Suggestions are heuristics. Apply opens the bulk-rate modal so you can tweak before pushing to Beds24.
            </p>
          </div>
        </div>
      )}

      {/* Bulk-rate-edit modal — opens after drag on group header cells */}
      {rateEdit && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => !rateEdit.saving && setRateEdit(null)}
          data-testid="cal-rate-edit-modal"
        >
          <div
            className="bg-white rounded-xl w-full max-w-sm p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-3">
              <div className="min-w-0">
                <h3 className="text-lg font-bold text-slate-900 truncate flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-emerald-600" />
                  Bulk rate edit
                </h3>
                <p className="text-xs text-slate-500 truncate">{rateEdit.row.room_name}</p>
              </div>
              <button
                type="button"
                onClick={() => !rateEdit.saving && setRateEdit(null)}
                className="p-1 rounded hover:bg-slate-100"
              >
                <ChevronLeft className="w-5 h-5 rotate-45" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <label className="block">
                <span className="text-[10px] uppercase font-semibold text-slate-500">From</span>
                <input
                  type="date"
                  value={rateEdit.start_iso}
                  onChange={(e) => setRateEdit({ ...rateEdit, start_iso: e.target.value })}
                  className="w-full text-sm border rounded px-2 py-1.5 mt-0.5"
                  data-testid="cal-re-start"
                />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase font-semibold text-slate-500">To (inclusive)</span>
                <input
                  type="date"
                  value={rateEdit.end_iso}
                  onChange={(e) => setRateEdit({ ...rateEdit, end_iso: e.target.value })}
                  className="w-full text-sm border rounded px-2 py-1.5 mt-0.5"
                  data-testid="cal-re-end"
                />
              </label>
            </div>
            <label className="block mb-4">
              <span className="text-[10px] uppercase font-semibold text-slate-500">Nightly rate (€)</span>
              <div className="relative mt-0.5">
                <Euro className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={rateEdit.price}
                  onChange={(e) => setRateEdit({ ...rateEdit, price: e.target.value })}
                  placeholder="e.g. 120"
                  className="w-full text-lg font-bold border rounded pl-8 pr-2 py-2"
                  autoFocus
                  data-testid="cal-re-price"
                />
              </div>
            </label>
            {(() => {
              const ci = new Date(rateEdit.start_iso + 'T00:00:00');
              const co = new Date(rateEdit.end_iso + 'T00:00:00');
              const nights = Math.max(0, Math.round((co - ci) / (1000 * 60 * 60 * 24)) + 1);
              const price = parseFloat(rateEdit.price) || 0;
              return (
                <div className="mb-3 p-2 rounded bg-slate-50 text-xs text-slate-600 flex justify-between">
                  <span>{nights} night{nights !== 1 ? 's' : ''} affected</span>
                  <span className="font-bold text-slate-800">€{price * nights} total pot</span>
                </div>
              );
            })()}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setRateEdit(null)}
                disabled={rateEdit.saving}
                className="flex-1 py-2 text-sm border rounded hover:bg-slate-50 disabled:opacity-50"
              >Cancel</button>
              <button
                type="button"
                onClick={submitRateEdit}
                disabled={rateEdit.saving || !rateEdit.price}
                className="flex-1 py-2 text-sm rounded bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="cal-re-submit"
              >
                {rateEdit.saving ? <><Loader2 className="w-4 h-4 animate-spin" />Saving…</> : 'Push to Beds24'}
              </button>
            </div>
            <p className="text-[10px] text-slate-400 mt-2">
              Rates propagate to every connected OTA within a few minutes.
            </p>
          </div>
        </div>
      )}

      {/* Guest history modal — deep-link from autocomplete */}
      {guestHistory && (
        <div
          className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4"
          onClick={() => setGuestHistory(null)}
          data-testid="cal-guest-history-modal"
        >
          <div
            className="bg-white rounded-xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white p-4 flex justify-between items-start">
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-widest opacity-90">Guest profile</p>
                <h3 className="text-xl font-bold truncate">{guestHistory.name}</h3>
                {guestHistory.data && !guestHistory.loading && (
                  <p className="text-xs opacity-90 mt-0.5">
                    {guestHistory.data.stays} stay{guestHistory.data.stays !== 1 ? 's' : ''} · lifetime spend €{guestHistory.data.total_spend}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => setGuestHistory(null)}
                className="p-1 rounded hover:bg-white/20"
              >
                <ChevronLeft className="w-5 h-5 rotate-45" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {guestHistory.loading ? (
                <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-orange-500" /></div>
              ) : (
                <>
                  {/* Tag editor — VIP / quiet / allergy chips + free-form */}
                  <div className="mb-4 pb-3 border-b" data-testid="cal-gh-tags">
                    <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">Tags · seen on the room-sheet</p>
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {(guestHistory.data?.tags || []).map((t, i) => {
                        const styleByKind = {
                          vip:      'bg-amber-100 text-amber-900 border-amber-300',
                          quiet:    'bg-sky-100 text-sky-900 border-sky-300',
                          allergic: 'bg-red-100 text-red-900 border-red-300',
                          late:     'bg-purple-100 text-purple-900 border-purple-300',
                          pillow:   'bg-indigo-100 text-indigo-900 border-indigo-300',
                          note:     'bg-slate-100 text-slate-800 border-slate-300',
                        }[t.kind] || 'bg-slate-100 text-slate-800 border-slate-300';
                        return (
                          <span key={i} className={`inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider border rounded-full px-2 py-0.5 ${styleByKind}`} data-testid={`cal-gh-tag-${i}`}>
                            {t.label}
                            <button
                              type="button"
                              onClick={() => saveGuestTags((guestHistory.data.tags || []).filter((_, idx) => idx !== i))}
                              className="hover:text-red-600 -mr-1"
                              title="Remove tag"
                            >×</button>
                          </span>
                        );
                      })}
                      {(guestHistory.data?.tags || []).length === 0 && (
                        <span className="text-xs text-slate-400 italic">No tags yet</span>
                      )}
                    </div>
                    {/* Quick-pick chips */}
                    <div className="flex flex-wrap gap-1 mb-2">
                      {[
                        { label: 'VIP', kind: 'vip' },
                        { label: 'Quiet floor', kind: 'quiet' },
                        { label: 'Allergy', kind: 'allergic' },
                        { label: 'Late check-out', kind: 'late' },
                        { label: 'Extra pillows', kind: 'pillow' },
                      ].map((sug) => {
                        const exists = (guestHistory.data?.tags || []).some((t) => t.label.toLowerCase() === sug.label.toLowerCase());
                        if (exists) return null;
                        return (
                          <button
                            key={sug.label}
                            type="button"
                            onClick={() => saveGuestTags([...(guestHistory.data?.tags || []), sug])}
                            className="text-[10px] font-semibold text-slate-500 hover:text-orange-700 hover:bg-orange-50 border border-dashed border-slate-300 rounded-full px-2 py-0.5"
                          >+ {sug.label}</button>
                        );
                      })}
                    </div>
                    <div className="flex gap-1">
                      <input
                        type="text"
                        value={newTag.label}
                        onChange={(e) => setNewTag({ ...newTag, label: e.target.value })}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && newTag.label.trim()) {
                            saveGuestTags([...(guestHistory.data?.tags || []), { label: newTag.label.trim(), kind: newTag.kind }]);
                          }
                        }}
                        placeholder="Custom tag…"
                        className="flex-1 text-xs border rounded px-2 py-1"
                        data-testid="cal-gh-tag-input"
                      />
                      <select
                        value={newTag.kind}
                        onChange={(e) => setNewTag({ ...newTag, kind: e.target.value })}
                        className="text-xs border rounded px-2 py-1"
                      >
                        <option value="note">Note</option>
                        <option value="vip">VIP</option>
                        <option value="quiet">Quiet</option>
                        <option value="allergic">Allergy</option>
                        <option value="late">Late</option>
                        <option value="pillow">Pillow</option>
                      </select>
                      <button
                        type="button"
                        onClick={() => newTag.label.trim() && saveGuestTags([...(guestHistory.data?.tags || []), { label: newTag.label.trim(), kind: newTag.kind }])}
                        disabled={!newTag.label.trim()}
                        className="text-xs px-2 py-1 rounded bg-orange-500 text-white font-semibold hover:bg-orange-600 disabled:opacity-40"
                        data-testid="cal-gh-tag-add"
                      >Add</button>
                    </div>
                  </div>
                  {(guestHistory.data?.history || []).length === 0 ? (
                    <p className="text-sm text-slate-500 italic text-center py-8">No past stays found.</p>
                  ) : (
                    <ol className="space-y-3">
                      {guestHistory.data.history.map((h, i) => (
                        <li key={i} className="border rounded-lg p-3 hover:border-orange-300" data-testid={`cal-gh-stay-${i}`}>
                          <div className="flex justify-between items-start mb-1">
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-slate-900 truncate">{h.room_name || 'Room'}</p>
                              <p className="text-xs text-slate-500">
                                {h.check_in} → {h.check_out}
                                {h.source ? ` · ${h.source}` : ''}
                                {h.status ? ` · ${h.status}` : ''}
                              </p>
                            </div>
                            <p className="text-sm font-bold text-emerald-700 shrink-0 ml-2">
                              €{Math.round(h.total || 0)}
                            </p>
                          </div>
                          {(h.email || h.phone) && (
                            <p className="text-[10px] text-slate-400 mt-1 truncate">
                              {h.email || ''}{h.email && h.phone ? ' · ' : ''}{h.phone || ''}
                            </p>
                          )}
                          {h.notes && (
                            <p className="text-xs italic text-slate-600 mt-1 border-t pt-1">"{h.notes}"</p>
                          )}
                        </li>
                      ))}
                    </ol>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* New-booking modal — opens when clicking any empty date cell */}
      {newBooking && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setNewBooking(null)}
          data-testid="cal-new-booking-modal"
        >
          <div
            className="bg-white rounded-xl w-full max-w-md p-5 shadow-2xl max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-3">
              <div className="min-w-0">
                <h3 className="text-lg font-bold text-slate-900 truncate">New booking</h3>
                <p className="text-xs text-slate-500 truncate">
                  {newBooking.lane.unit_name ? `Room ${newBooking.lane.unit_name}` : `${newBooking.row.room_name} · unit ${newBooking.lane.unit_number}`}
                </p>
              </div>
              <button onClick={() => setNewBooking(null)} className="p-1 rounded hover:bg-slate-100">
                <ChevronLeft className="w-5 h-5 rotate-45" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <label className="block">
                <span className="text-[10px] uppercase font-semibold text-slate-500">Check-in</span>
                <input
                  type="date"
                  value={newBooking.start_iso}
                  onChange={(e) => setNewBooking({ ...newBooking, start_iso: e.target.value })}
                  className="w-full text-sm border rounded px-2 py-1.5 mt-0.5"
                  data-testid="cal-nb-checkin"
                />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase font-semibold text-slate-500">Check-out</span>
                <input
                  type="date"
                  value={newBooking.end_iso}
                  onChange={(e) => setNewBooking({ ...newBooking, end_iso: e.target.value })}
                  className="w-full text-sm border rounded px-2 py-1.5 mt-0.5"
                  data-testid="cal-nb-checkout"
                />
              </label>
            </div>
            <label className="block mb-2 relative">
              <span className="text-[10px] uppercase font-semibold text-slate-500">Guest name *</span>
              <input
                type="text"
                value={nbForm.guest_name}
                onChange={(e) => setNbForm({ ...nbForm, guest_name: e.target.value })}
                placeholder="e.g. Marco Rossi"
                className="w-full text-sm border rounded px-2 py-1.5 mt-0.5"
                autoFocus
                data-testid="cal-nb-guest"
              />
              {nbSuggestions.length > 0 && (
                <ul
                  className="absolute z-40 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto"
                  data-testid="cal-nb-suggest"
                >
                  {nbSuggestions.map((s, i) => (
                    <li
                      key={`${s.guest_name}-${i}`}
                      className="px-3 py-2 hover:bg-orange-50 border-b border-slate-100 last:border-0 flex items-start gap-2"
                      data-testid={`cal-nb-suggest-${i}`}
                    >
                      <div
                        onClick={() => {
                          setNbForm({
                            ...nbForm,
                            guest_name: s.guest_name,
                            email: s.email || nbForm.email,
                            phone: s.phone || nbForm.phone,
                            adults: s.adults || nbForm.adults,
                            children: s.children || nbForm.children,
                          });
                          setNbSuggestions([]);
                        }}
                        className="flex-1 cursor-pointer min-w-0"
                      >
                        <p className="text-sm font-medium text-slate-800 truncate">{s.guest_name}</p>
                        <p className="text-[10px] text-slate-500 truncate">
                          {s.email || ''} {s.phone ? ` · ${s.phone}` : ''}
                          {s.last_stay ? ` · last stay ${s.last_stay}` : ''}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); openGuestHistory(s.guest_name); }}
                        className="shrink-0 p-1 text-slate-400 hover:text-orange-600 hover:bg-orange-50 rounded"
                        title="Open full booking history & notes"
                        data-testid={`cal-nb-suggest-history-${i}`}
                      >
                        <ExternalLink className="w-4 h-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </label>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <label className="block">
                <span className="text-[10px] uppercase font-semibold text-slate-500">Adults</span>
                <input
                  type="number" min="1" value={nbForm.adults}
                  onChange={(e) => setNbForm({ ...nbForm, adults: e.target.value })}
                  className="w-full text-sm border rounded px-2 py-1.5 mt-0.5"
                />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase font-semibold text-slate-500">Children</span>
                <input
                  type="number" min="0" value={nbForm.children}
                  onChange={(e) => setNbForm({ ...nbForm, children: e.target.value })}
                  className="w-full text-sm border rounded px-2 py-1.5 mt-0.5"
                />
              </label>
            </div>
            <label className="block mb-2">
              <span className="text-[10px] uppercase font-semibold text-slate-500">Email</span>
              <input
                type="email" value={nbForm.email}
                onChange={(e) => setNbForm({ ...nbForm, email: e.target.value })}
                className="w-full text-sm border rounded px-2 py-1.5 mt-0.5"
              />
            </label>
            <label className="block mb-2">
              <span className="text-[10px] uppercase font-semibold text-slate-500">Phone</span>
              <input
                type="tel" value={nbForm.phone}
                onChange={(e) => setNbForm({ ...nbForm, phone: e.target.value })}
                className="w-full text-sm border rounded px-2 py-1.5 mt-0.5"
              />
            </label>
            <label className="block mb-4">
              <span className="text-[10px] uppercase font-semibold text-slate-500">Notes</span>
              <textarea
                value={nbForm.notes}
                onChange={(e) => setNbForm({ ...nbForm, notes: e.target.value })}
                rows="2"
                className="w-full text-sm border rounded px-2 py-1.5 mt-0.5"
              />
            </label>
            {(() => {
              // Rate preview — sum the daily rates between check_in and check_out.
              const roomRates = rates[newBooking.row.room_id] || {};
              let total = 0, nights = 0, missing = false;
              try {
                const start = new Date(newBooking.start_iso + 'T00:00:00');
                const end = new Date(newBooking.end_iso + 'T00:00:00');
                for (let d = new Date(start); d < end; d.setDate(d.getDate() + 1)) {
                  const iso = d.toISOString().slice(0, 10);
                  const p = roomRates[iso];
                  if (p == null) missing = true;
                  else total += p;
                  nights += 1;
                }
              } catch { /* invalid dates */ }
              if (nights <= 0) return null;
              return (
                <div className="mb-4 p-3 rounded-lg bg-gradient-to-br from-emerald-50 to-emerald-100 border border-emerald-200" data-testid="cal-nb-rate-preview">
                  <div className="flex items-baseline justify-between">
                    <div>
                      <p className="text-[10px] uppercase font-bold tracking-wider text-emerald-700">Rate preview</p>
                      <p className="text-xs text-emerald-800">
                        {nights} night{nights !== 1 ? 's' : ''}{total > 0 ? ` · €${Math.round(total / nights)}/night` : ''}
                      </p>
                    </div>
                    <p className="text-2xl font-bold text-emerald-900">
                      {total > 0 ? `€${Math.round(total)}` : '—'}
                    </p>
                  </div>
                  {missing && (
                    <p className="text-[10px] text-amber-700 mt-1 italic">
                      Some dates have no rate set in Beds24 — total may be incomplete.
                    </p>
                  )}
                </div>
              );
            })()}
            <div className="flex gap-2">
              <button
                onClick={() => setNewBooking(null)}
                className="flex-1 py-2 text-sm border rounded hover:bg-slate-50"
              >Cancel</button>
              <button
                onClick={submitNewBooking}
                disabled={nbSaving || !nbForm.guest_name.trim()}
                className="flex-1 py-2 text-sm rounded bg-orange-600 text-white font-semibold hover:bg-orange-700 disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="cal-nb-submit"
              >
                {nbSaving ? <><Loader2 className="w-4 h-4 animate-spin" />Saving…</> : 'Create booking'}
              </button>
            </div>
            <p className="text-[10px] text-slate-400 mt-2">
              Booking is pushed to Beds24 and shows on all connected OTAs.
            </p>
          </div>
        </div>
      )}

      {/* Inline housekeeping status picker — Beds24-style tap-to-change */}
      {notesPopover}
      <CalendarContextMenu menu={menu} folioStatus={menuStatus} busy={menuBusy} onAction={onMenuAction} onClose={() => setMenu(null)} />
      {quickMove && (
        <RoomMoveModal bookingId={quickMove.booking_id} guestName={quickMove.guest_name} onClose={() => setQuickMove(null)} onMoved={() => load()} />
      )}
      {quickMsg && (
        <GuestMessageModal bookingId={quickMsg.booking_id} guestName={quickMsg.guest_name} onClose={() => setQuickMsg(null)} />
      )}
      {statusPicker && (() => {
        const HK_ORDER = ['clean', 'cleaning', 'dirty', 'to_repair'];
        const HK_LABEL = { clean: 'Clean', cleaning: 'Cleaning…', dirty: 'Dirty', to_repair: 'To repair' };
        const HK_CLR = {
          clean:     'bg-emerald-50 border-emerald-300 text-emerald-800 hover:bg-emerald-100',
          cleaning:  'bg-sky-50 border-sky-300 text-sky-800 hover:bg-sky-100',
          dirty:     'bg-amber-50 border-amber-400 text-amber-900 hover:bg-amber-100',
          to_repair: 'bg-yellow-50 border-yellow-400 text-yellow-900 hover:bg-yellow-100',
        };
        const lane = statusPicker.lane;
        const row = statusPicker.row;
        const label = lane.unit_name ? `Room ${lane.unit_name}` : `${row.room_name} · unit ${lane.unit_number}`;
        return (
          <div
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setStatusPicker(null)}
            data-testid="cal-status-picker"
          >
            <div
              className="bg-white rounded-xl w-full max-w-sm p-5 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-3">
                <div className="min-w-0">
                  <h3 className="text-lg font-bold text-slate-900 truncate">{label}</h3>
                  <p className="text-xs text-slate-500 truncate">{row.room_name}</p>
                </div>
                <button onClick={() => setStatusPicker(null)} className="p-1 rounded hover:bg-slate-100">
                  <ChevronLeft className="w-5 h-5 rotate-45" />
                </button>
              </div>
              <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">
                Change housekeeping status
              </p>
              <div className="grid grid-cols-2 gap-2">
                {HK_ORDER.map((s) => (
                  <button
                    key={s}
                    onClick={() => setUnitStatus(lane.unit_key, s)}
                    className={`p-4 min-h-[60px] rounded-lg border-2 font-semibold text-sm active:scale-[0.97] touch-manipulation ${HK_CLR[s]}`}
                    data-testid={`cal-hk-set-${s}`}
                  >
                    {HK_LABEL[s]}
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-slate-400 mt-3">
                Changes sync to the Housekeeping board and log to the activity feed.
              </p>
            </div>
          </div>
        );
      })()}

      {/* Booking detail drawer */}
      {selected && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex justify-end"
          onClick={() => setSelected(null)}
          data-testid="cal-drawer"
        >
          <div
            className="w-full max-w-sm bg-white h-full overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`${srcColor(selected.source).bg} text-white p-4`}>
              <p className="text-xs uppercase tracking-widest opacity-90">Booking</p>
              <h3 className="text-lg font-bold mt-1" data-testid="cal-drawer-name">{selected.guest_name}</h3>
              <p className="text-xs opacity-90 mt-1">Source: {selected.source}</p>
            </div>
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <p className="text-xs text-slate-500">Check-in</p>
                  <p className="font-semibold text-slate-900">{selected.check_in}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Check-out</p>
                  <p className="font-semibold text-slate-900">{selected.check_out}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Adults</p>
                  <p className="font-semibold text-slate-900">{selected.adults ?? '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Children</p>
                  <p className="font-semibold text-slate-900">{selected.children ?? 0}</p>
                </div>
              </div>
              <div>
                <p className="text-xs text-slate-500">Booking ID</p>
                <p className="font-mono text-xs text-slate-700 break-all">{selected.booking_id}</p>
              </div>
              <button
                onClick={() => cancelBooking(selected.booking_id, selected.guest_name)}
                className="w-full py-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-700 text-sm font-semibold border border-red-200"
                data-testid="cal-drawer-cancel"
              >
                Cancel booking
              </button>
              <button
                onClick={() => setSelected(null)}
                className="w-full mt-2 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarDashboard;
